import React, { useEffect } from "react";
import { Routes, Route, NavLink, useLocation } from "react-router-dom";
import { LegalLinkItems } from "../legal/legalLinks";
import BarcodeLabeler from "./BarcodeLabeler";
import ChestnyZnakTool from "./ChestnyZnakTool";
import OzonFbsLabelSorter from "./OzonFbsLabelSorter";
import AiDescGenerator from "./AiDescGenerator";
import { useTheme } from "../../contexts/ThemeContext";
import { IconSun, IconMoon } from "../../components/icons";
import "./FreeFunctionsPage.css";

// ─── SEO helpers ─────────────────────────────────────────────────────────────

function useMeta({ title, description, canonical }) {
  useEffect(() => {
    document.title = title;

    let desc = document.querySelector('meta[name="description"]');
    if (!desc) { desc = document.createElement("meta"); desc.name = "description"; document.head.appendChild(desc); }
    desc.content = description;

    let og = document.querySelector('meta[property="og:title"]');
    if (!og) { og = document.createElement("meta"); og.setAttribute("property", "og:title"); document.head.appendChild(og); }
    og.content = title;

    let ogD = document.querySelector('meta[property="og:description"]');
    if (!ogD) { ogD = document.createElement("meta"); ogD.setAttribute("property", "og:description"); document.head.appendChild(ogD); }
    ogD.content = description;

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = canonical;

    return () => { document.title = "Энилекс — контроль маркетплейсов"; };
  }, [title, description, canonical]);
}

function useJsonLd(schema) {
  useEffect(() => {
    const id = "jsonld-free-tools";
    let el = document.getElementById(id);
    if (!el) { el = document.createElement("script"); el.id = id; el.type = "application/ld+json"; document.head.appendChild(el); }
    el.textContent = JSON.stringify(schema);
    return () => { el.remove(); };
  }, [schema]);
}

// ─── Tools registry ───────────────────────────────────────────────────────────

const TOOLS = [
  {
    path: "barcode",
    label: "Создание ШК и QR, создание дублей",
    description: "Печать этикетки по значению или со сканера, название, даты — без регистрации",
    icon: "▦",
    ready: true,
  },
  {
    path: "chestny-znak",
    label: "Работа с Честным знаком",
    description: "Печать и дублирование Data Matrix Честного знака: сканирование камерой или сканером, преобразование КИЗ в корректный формат с GS-разделителями",
    icon: "◫",
    ready: true,
  },
  {
    path: "ozon-fbs-labels",
    label: "Сортировка FBS-этикеток Ozon",
    description: "Загрузите PDF этикеток и assembly list, затем получите одну ленту, пересобранную по артикулам прямо в браузере",
    icon: "⇅",
    ready: true,
  },
  {
    path: "ai-generator",
    label: "AI-генератор названия и описания",
    description: "Генерация SEO-оптимизированных названий и описаний для Ozon и Wildberries через GigaChat. Используйте свой API-ключ — ничего не хранится у нас.",
    icon: "✦",
    ready: true,
  },
];

const COMING_SOON = [
  {
    label: "Подойдёт ли товар для маркетплейса",
    description: "Маркетинговый опросник: анализ ниши, конкуренции, маржинальности и готовности товара к запуску на Wildberries или Ozon.",
    icon: "◎",
  },
];

// ─── Honeypot + timing bot protection ────────────────────────────────────────

export function useBotCheck() {
  const loadTime = React.useRef(Date.now());
  const [honey, setHoney] = React.useState("");

  const isBot = () => {
    if (honey.length > 0) return true;                          // honeypot filled
    if (Date.now() - loadTime.current < 1500) return true;     // too fast
    return false;
  };

  const HoneypotField = () => (
    <input
      type="text"
      name="website"
      value={honey}
      onChange={(e) => setHoney(e.target.value)}
      style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none", tabIndex: -1 }}
      autoComplete="off"
      aria-hidden="true"
    />
  );

  return { isBot, HoneypotField };
}

// ─── Hub page ─────────────────────────────────────────────────────────────────

const HUB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Бесплатные инструменты для маркетплейсов — Энилекс",
  description: "Бесплатные онлайн-инструменты для продавцов на Wildberries и Ozon. Работают в браузере, без регистрации, без сбора данных.",
  url: "https://seller.anylex.ru/free-functions",
  provider: { "@type": "Organization", name: "Энилекс", url: "https://anylex.ru" },
  mainEntity: {
    "@type": "ItemList",
    itemListElement: TOOLS.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.label,
      description: t.description,
      url: `https://seller.anylex.ru/free-functions/${t.path}`,
    })),
  },
};

function ToolHub() {
  useMeta({
    title: "Бесплатные инструменты для маркетплейсов — Энилекс",
    description: "Печать штрихкодов, QR-кодов, дублей этикеток, AI-описания товаров и другие бесплатные инструменты для продавцов на Wildberries и Ozon. Без регистрации и без сбора данных.",
    canonical: "https://seller.anylex.ru/free-functions",
  });
  useJsonLd(HUB_SCHEMA);

  return (
    <div className="ff__hub">
      {/* Hero */}
      <div className="ff__hub-hero">
        <h1 className="ff__hub-h1">Бесплатные инструменты<br />для маркетплейсов</h1>
        <p className="ff__hub-lead">
          Работают прямо в браузере — без регистрации и совершенно бесплатно.
          Подходят для продавцов на Wildberries, Ozon, Яндекс Маркет.
        </p>
        <div className="ff__hub-badges">
          <span className="ff__badge ff__badge--green">Без регистрации</span>
          <span className="ff__badge ff__badge--green">Бесплатно</span>
          <span className="ff__badge ff__badge--blue">Данные не уходят на сервер</span>
          <span className="ff__badge ff__badge--blue">Персональные данные не собираем</span>
          <span className="ff__badge ff__badge--gray">Только cookies и анонимные метрики</span>
        </div>
      </div>

      {/* Tools */}
      <section className="ff__hub-section">
        <h2 className="ff__hub-h2">Доступные инструменты</h2>
        <div className="ff__hub-grid">
          {TOOLS.map((t) => (
            <NavLink key={t.path} to={t.path} className="ff__hub-card ff__hub-card--ready">
              <span className="ff__hub-icon">{t.icon}</span>
              <div>
                <div className="ff__hub-card-title">{t.label}</div>
                <div className="ff__hub-card-desc">{t.description}</div>
              </div>
            </NavLink>
          ))}
        </div>
      </section>

      {/* Coming soon */}
      <section className="ff__hub-section">
        <h2 className="ff__hub-h2">Скоро</h2>
        <div className="ff__hub-grid">
          {COMING_SOON.map((t) => (
            <div key={t.label} className="ff__hub-card ff__hub-card--soon">
              <span className="ff__hub-icon">{t.icon}</span>
              <div>
                <div className="ff__hub-card-title">{t.label} <span className="ff__soon-tag">скоро</span></div>
                <div className="ff__hub-card-desc">{t.description}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Privacy block */}
      <section className="ff__hub-section ff__privacy">
        <h2 className="ff__hub-h2">Прозрачность и приватность</h2>
        <div className="ff__privacy-grid">
          <div className="ff__privacy-item">
            <div className="ff__privacy-icon">🔒</div>
            <div className="ff__privacy-text">
              <strong>Всё в вашем браузере</strong>
              <span>Ни штрихкоды, ни тексты, ни файлы не отправляются на наш сервер. Вычисления и генерация происходят локально.</span>
            </div>
          </div>
          <div className="ff__privacy-item">
            <div className="ff__privacy-icon">🍪</div>
            <div className="ff__privacy-text">
              <strong>Только cookies и метрики</strong>
              <span>Сохраняем только технические cookies (сессия, настройки) и анонимную аналитику просмотров. Без ID, без поведенческого профиля.</span>
            </div>
          </div>
          <div className="ff__privacy-item">
            <div className="ff__privacy-icon">🚫</div>
            <div className="ff__privacy-text">
              <strong>Персональные данные не собираем</strong>
              <span>Регистрация не требуется. Вы не вводите имя, email или телефон. Мы не знаем, кто вы — и не стремимся узнать.</span>
            </div>
          </div>
          <div className="ff__privacy-item">
            <div className="ff__privacy-icon">🤖</div>
            <div className="ff__privacy-text">
              <strong>Защита от роботов</strong>
              <span>Используем ненавязчивые проверки поведения пользователя без CAPTCHA. Данные этих проверок не сохраняются.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Who is it for */}
      <section className="ff__hub-section">
        <h2 className="ff__hub-h2">Для кого эти инструменты</h2>
        <div className="ff__audience-grid">
          {[
            { who: "Продавцы на Ozon", what: "Печать этикеток для FBO-поставок, создание дублей маркировки" },
            { who: "Продавцы на Wildberries", what: "Подготовка ШК-этикеток, работа со сканером без ПО" },
            { who: "Малый бизнес и ИП", what: "Маркировка продукции питания: дата производства, срок годности" },
            { who: "Логисты и кладовщики", what: "Быстрая дубликация штрихкодов на складе без установки программ" },
          ].map((a) => (
            <div key={a.who} className="ff__audience-item">
              <div className="ff__audience-who">{a.who}</div>
              <div className="ff__audience-what">{a.what}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Hub FAQ for GEO */}
      <section className="ff__hub-section">
        <h2 className="ff__hub-h2">Вопросы и ответы</h2>
        <div className="ff__seo-faq">
          {[
            ["Нужна ли регистрация?", "Нет. Все инструменты работают без создания аккаунта. Достаточно открыть страницу в браузере."],
            ["Мои данные уходят на ваш сервер?", "Нет. Все операции выполняются в вашем браузере: штрихкоды генерируются локально, файлы не загружаются, тексты не сохраняются."],
            ["Платить нужно?", "Инструменты полностью бесплатны. Расширенные функции доступны в платном сервисе Энилекс, но это отдельно и без обязательств."],
            ["Работают ли инструменты без интернета?", "После первой загрузки страницы большинство функций (генерация ШК, QR, PDF) работают офлайн — браузер кэширует необходимые скрипты."],
            ["Какие браузеры поддерживаются?", "Любой современный браузер: Chrome, Firefox, Edge, Safari. Для печати этикеток рекомендуем Chrome — он лучше поддерживает @page CSS для термопринтеров."],
          ].map(([q, a]) => (
            <details key={q} className="ff__seo-qa">
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── Barcode tool SEO ─────────────────────────────────────────────────────────

const BARCODE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Генератор штрихкодов и QR-кодов онлайн — Энилекс",
  description: "Бесплатный онлайн-генератор штрихкодов: QR, Code 128, EAN-13, EAN-8, UPC, ITF-14. Печать этикеток с датами и названием. Работает в браузере без регистрации.",
  url: "https://seller.anylex.ru/free-functions/barcode",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web browser",
  offers: { "@type": "Offer", price: "0", priceCurrency: "RUB" },
  provider: { "@type": "Organization", name: "Энилекс", url: "https://anylex.ru" },
  featureList: [
    "Генерация QR-кода онлайн",
    "Генерация Code 128 штрихкода",
    "Генерация EAN-13 штрихкода",
    "Печать этикеток на термопринтере",
    "Сохранение этикетки в PDF",
    "Дата производства и срок годности на этикетке",
    "Автоопределение ввода сканера штрихкодов",
  ],
};

function BarcodeSeoSection() {
  return (
    <div className="ff__seo">
      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Как пользоваться генератором штрихкодов и QR-кодов</h2>
        <ol className="ff__seo-list ff__seo-list--ol">
          <li>Выберите <strong>формат</strong>: QR-код, Code 128, EAN-13 или другой — в зависимости от типа маркировки.</li>
          <li>Выберите <strong>размер этикетки</strong> под ваш термопринтер (58×40, 120×75 мм и другие).</li>
          <li>Введите значение в поле или <strong>поднесите сканер</strong> — этикетка уйдёт в печать автоматически.</li>
          <li>Опционально: добавьте название товара и даты (производство, срок годности, годен до).</li>
          <li>Нажмите <strong>«Печать»</strong> или <strong>Enter</strong> — откроется диалог принтера.</li>
          <li>Для сохранения в PDF: нажмите <strong>«Сохранить PDF»</strong> и выберите «Сохранить как PDF» в диалоге.</li>
        </ol>
        <p className="ff__seo-note">Всё обрабатывается в браузере — ничего не отправляется на сервер.</p>
      </section>

      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Поддерживаемые форматы штрихкодов</h2>
        <div className="ff__seo-formats">
          {[
            ["QR-код", "Универсальный 2D-код. Поддерживает текст, URL, номера. Используется в Честном знаке, маркировке молочной продукции, одежды, обуви."],
            ["Code 128", "Самый распространённый 1D-штрихкод. Поддерживает цифры и буквы. Применяется для артикулов, внутренней маркировки, ШК коробок."],
            ["EAN-13", "Стандарт для товаров в розничной торговле. Ровно 13 цифр. Используется на полках магазинов и для поставок на Wildberries и Ozon."],
            ["EAN-8, UPC-A, UPC-E", "Укороченные форматы для небольших упаковок и рынка США. EAN-8 — 8 цифр, UPC — стандарт Северной Америки."],
            ["ITF-14", "Используется для транспортных упаковок (коробов, паллет). Стандарт GS1 — 14 цифр. Часто требуется для FBO-поставок на склады маркетплейсов."],
            ["Code 39", "Старый, но надёжный линейный код. Поддерживает цифры и заглавные латинские буквы. Применяется в промышленности и логистике."],
          ].map(([name, desc]) => (
            <div key={name} className="ff__seo-format">
              <div className="ff__seo-format-name">{name}</div>
              <div className="ff__seo-format-desc">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Применение: когда нужно печатать этикетки</h2>
        <ul className="ff__seo-list">
          <li><strong>Дубль маркировки Честного знака</strong> — если этикетка повреждена, быстро распечатайте дубль по коду без сканирования в системе.</li>
          <li><strong>Подготовка товара для FBO-поставки</strong> на Ozon или Wildberries — нанесение артикула и штрихкода на коробку.</li>
          <li><strong>Маркировка продуктов питания</strong> — дата производства, срок годности и наименование прямо на термоэтикетке.</li>
          <li><strong>Внутренняя маркировка склада</strong> — нанесение ШК на ячейки, паллеты, коробки для инвентаризации.</li>
          <li><strong>Замена повреждённой этикетки</strong> — если ШК не читается сканером, создайте новую по известному значению.</li>
          <li><strong>Мелкосерийное производство</strong> — печать небольших партий этикеток без промышленного ПО.</li>
        </ul>
      </section>

      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Часто задаваемые вопросы</h2>
        <div className="ff__seo-faq">
          {[
            ["Данные отправляются на сервер?", "Нет. Всё генерируется локально в вашем браузере с помощью библиотек qrcode и JsBarcode. Ни коды, ни даты никуда не передаются."],
            ["Какой принтер нужен?", "Подойдёт любой термопринтер, поддерживаемый вашей ОС: Zebra, HPRT, TSC, Honeywell, Godex и другие. Настройте принтер по умолчанию в системе."],
            ["Как сканер связан с автопечатью?", "Сканеры штрихкодов передают данные как быстрый ввод с клавиатуры, заканчивающийся Enter. Инструмент распознаёт этот паттерн и запускает печать автоматически."],
            ["Как работает расчёт сроков годности?", "Если указать «Дата производства» и «Срок годности», поле «Годен до» вычисляется автоматически. И наоборот. При несоответствии появится предупреждение."],
            ["Можно напечатать несколько копий?", "Да. Укажите количество копий — откроется документ с нужным числом этикеток, каждая на отдельной странице."],
            ["Сохранение в PDF работает офлайн?", "Да. Функция «Сохранить PDF» использует встроенный механизм браузера — интернет не нужен."],
          ].map(([q, a]) => (
            <details key={q} className="ff__seo-qa">
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── Page shell ───────────────────────────────────────────────────────────────

export default function FreeFunctionsPage() {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="ff">
      <header className="ff__header">
        <NavLink to="/" className="ff__logo">
          <span className="ff__logo-text">Энилекс</span>
        </NavLink>
        <nav className="ff__nav" aria-label="Инструменты">
          <NavLink to="/free-functions" end className="ff__nav-home">
            Инструменты
          </NavLink>
          {TOOLS.map((t) => (
            <NavLink
              key={t.path}
              to={`/free-functions/${t.path}`}
              className={({ isActive }) => `ff__nav-link${isActive ? " ff__nav-link--active" : ""}`}
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
        <button
          className="ff__theme-toggle"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Переключить на светлую тему" : "Переключить на тёмную тему"}
          title={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
        >
          {theme === "dark" ? <IconSun /> : <IconMoon />}
        </button>
        <NavLink to="/" className="ff__cta">
          Войти в сервис →
        </NavLink>
      </header>

      <main className="ff__main" id="main-content">
        <Routes>
          <Route index element={<ToolHub />} />
          <Route
            path="barcode"
            element={
              <>
                {/* Inline meta for barcode tool */}
                <MetaBarcode />
                <div className="ff__tool-header">
                  <h1 className="ff__tool-title">Создание ШК и QR, создание дублей</h1>
                  <p className="ff__tool-desc">
                    Введите значение вручную или поднесите сканер — этикетка сразу уйдёт на печать.
                    Добавьте название товара и даты (производство, годность, срок хранения).
                    Подходит для дублирования маркировки Честного знака, артикулов, ШК товара, подготовки товара для FBO поставок.
                  </p>
                </div>
                <BarcodeLabeler />
                <BarcodeSeoSection />
              </>
            }
          />
          <Route
            path="chestny-znak"
            element={
              <>
                <MetaChestnyZnak />
                <div className="ff__tool-header">
                  <h1 className="ff__tool-title">Работа с Честным знаком: печать и дубли Data Matrix</h1>
                  <p className="ff__tool-desc">
                    Отсканируйте код маркировки камерой или сканером — этикетка Data Matrix (GS1)
                    сразу уйдёт на печать. Инструмент распознаёт GS-разделители (F8, Alt+0029),
                    исправляет раскладку клавиатуры и преобразует КИЗ в корректный вид.
                    Подходит для дублирования повреждённой маркировки Честного знака.
                  </p>
                </div>
                <ChestnyZnakTool />
                <ChestnyZnakSeoSection />
              </>
            }
          />
          <Route
            path="ozon-fbs-labels"
            element={
              <>
                <MetaOzonFbsLabels />
                <div className="ff__tool-header">
                  <h1 className="ff__tool-title">Сортировка FBS-этикеток Ozon по артикулам</h1>
                  <p className="ff__tool-desc">
                    Загрузите PDF со списком отправлений и PDF с этикетками. Инструмент
                    локально пересоберет одну ленту: разделитель с товаром и артикулом,
                    затем исходные этикетки этой группы. Сборные заказы уйдут в конец.
                  </p>
                </div>
                <OzonFbsLabelSorter />
                <OzonFbsSeoSection />
              </>
            }
          />
          <Route
            path="ai-generator"
            element={
              <>
                <MetaAiGenerator />
                <div className="ff__tool-header">
                  <h1 className="ff__tool-title">AI-генератор названий и описаний для маркетплейсов</h1>
                  <p className="ff__tool-desc">
                    Генерация SEO-оптимизированных названий, описаний и ключевых запросов для Ozon и Wildberries
                    через GigaChat. Вставьте товары из Excel или добавьте вручную. Ключ хранится только в вашем браузере.
                  </p>
                </div>
                <AiDescGenerator />
              </>
            }
          />
        </Routes>
      </main>

      <footer className="ff__footer">
        <span>© {new Date().getFullYear()} Энилекс — инструменты для маркетплейсов</span>
        <NavLink to="/">Главная</NavLink>
        <NavLink to="/blog">Блог</NavLink>
        <LegalLinkItems short />
      </footer>
    </div>
  );
}

const OZON_FBS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Сортировка FBS-этикеток Ozon по артикулам — Энилекс",
  description: "Бесплатный онлайн-инструмент для пересортировки FBS-этикеток Ozon. Загрузите PDF этикеток и assembly list — получите одну ленту по артикулам прямо в браузере без регистрации.",
  url: "https://seller.anylex.ru/free-functions/ozon-fbs-labels",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web browser",
  offers: { "@type": "Offer", price: "0", priceCurrency: "RUB" },
  provider: { "@type": "Organization", name: "Энилекс", url: "https://anylex.ru" },
  featureList: [
    "Сортировка FBS-этикеток Ozon по артикулам",
    "Группировка этикеток в одну PDF-ленту",
    "Разделители с товаром и артикулом между группами",
    "Поддержка сборных заказов",
    "Работает в браузере без загрузки на сервер",
  ],
};

function MetaOzonFbsLabels() {
  useMeta({
    title: "Сортировка FBS-этикеток Ozon по артикулам онлайн — Энилекс",
    description: "Бесплатная пересортировка FBS-этикеток Ozon: загрузите PDF этикеток и assembly list, получите одну ленту по артикулам. Работает в браузере без регистрации, файлы не уходят на сервер.",
    canonical: "https://seller.anylex.ru/free-functions/ozon-fbs-labels",
  });
  useJsonLd(OZON_FBS_SCHEMA);
  return null;
}

function OzonFbsSeoSection() {
  return (
    <div className="ff__seo">
      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Как пользоваться инструментом сортировки FBS-этикеток</h2>
        <ol className="ff__seo-list ff__seo-list--ol">
          <li>В личном кабинете Ozon скачайте <strong>PDF с этикетками</strong> — файл, где каждая страница содержит QR-код и номер отправления.</li>
          <li>Скачайте <strong>PDF со списком отправлений</strong> (assembly list) из того же раздела Ozon.</li>
          <li>Загрузите оба файла в соответствующие поля на странице.</li>
          <li>Нажмите <strong>«Разобрать файлы»</strong> — инструмент сопоставит этикетки со строками assembly list.</li>
          <li>Проверьте результат в блоке «Что найдено» — количество групп, этикеток, сборных заказов.</li>
          <li>Нажмите <strong>«Скачать итоговый PDF»</strong> — получите ленту этикеток, сгруппированных по артикулам.</li>
        </ol>
        <p className="ff__seo-note">Файлы обрабатываются только в вашем браузере — ничего не загружается на сервер.</p>
      </section>

      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Что находится в итоговом PDF</h2>
        <div className="ff__seo-formats">
          {[
            ["Разделитель группы", "Страница с названием товара и артикулом перед группой этикеток. Помогает отсортировать стопку этикеток на складе по одному артикулу."],
            ["Этикетки группы", "Оригинальные страницы Ozon-этикеток в исходном виде — QR и текст не перерисовываются. Меняется только порядок страниц."],
            ["Сборные заказы в конце", "Заказы с несколькими позициями или количеством > 1 выносятся в отдельный блок в конце ленты с подробным разделителем."],
            ["Автоматическая нумерация", "Страницы-разделители содержат счётчик — сколько этикеток в группе, что удобно для ручной проверки перед отгрузкой."],
          ].map(([name, desc]) => (
            <div key={name} className="ff__seo-format">
              <div className="ff__seo-format-name">{name}</div>
              <div className="ff__seo-format-desc">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Когда нужна сортировка FBS-этикеток</h2>
        <ul className="ff__seo-list">
          <li><strong>Много артикулов в одной поставке</strong> — этикетки из Ozon идут вперемешку, а на сборке нужна группировка по товару.</li>
          <li><strong>Ручная сборка без WMS</strong> — сотрудник склада не работает с системой управления складом, ему нужна физическая стопка по артикулу.</li>
          <li><strong>Большой объём FBS-заказов</strong> — при 100+ отправлениях в день сортировка вручную занимает 20–40 минут; инструмент сокращает это время до секунд.</li>
          <li><strong>Сборные заказы в конце</strong> — заказы с несколькими позициями требуют отдельного внимания; инструмент автоматически их выделяет.</li>
        </ul>
      </section>

      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Часто задаваемые вопросы</h2>
        <div className="ff__seo-faq">
          {[
            ["Файлы уходят на сервер?", "Нет. Оба PDF читаются и обрабатываются только в браузере с помощью библиотек pdf-lib и pdfjs-dist. На наш сервер не отправляется ничего."],
            ["Какой PDF с этикетками нужен?", "Зайдите в раздел FBS → «Ожидают отгрузки», выделите нужные товары и нажмите кнопку «Этикетки 58×40» или «Скачать» → «Этикетки». Каждая страница — одна этикетка с QR-кодом и номером отправления."],
            ["Что такое список отправлений (assembly list)?", "Зайдите в раздел FBS → «Ожидают сборки», выделите товары и нажмите «Лист подбора» → «По отправлениям» или «Скачать» → «Лист подбора» → «По отправлениям». Этот PDF содержит строки: товар, артикул, количество, номер отправления."],
            ["Сборные заказы поддерживаются?", "Да. Если в заказе несколько позиций или количество > 1, он определяется как сборный и выносится в конец с отдельным разделителем, содержащим полный список позиций."],
            ["Что делать, если появилось сообщение об ошибке?", "Проверьте, что оба PDF скачаны из актуального Ozon Seller. Иногда помогает скачать файлы заново — Ozon периодически меняет внутренний формат PDF."],
            ["Сохраняются ли настройки между сессиями?", "Нет. Файлы не кешируются и не сохраняются. При каждом использовании нужно загружать PDF заново."],
          ].map(([q, a]) => (
            <details key={q} className="ff__seo-qa">
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

function MetaBarcode() {
  useMeta({
    title: "Генератор штрихкодов и QR-кодов онлайн бесплатно — Энилекс",
    description: "Бесплатный онлайн-генератор штрихкодов и QR-кодов. Печать этикеток с датой производства и сроком годности. QR, Code 128, EAN-13, ITF-14. Без регистрации, данные не сохраняются.",
    canonical: "https://seller.anylex.ru/free-functions/barcode",
  });
  useJsonLd(BARCODE_SCHEMA);
  return null;
}

// ─── Честный знак tool SEO ──────────────────────────────────────────────────

const CHESTNY_ZNAK_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Работа с Честным знаком онлайн: печать и дубли Data Matrix — Энилекс",
  description: "Бесплатный онлайн-инструмент для печати и дублирования Data Matrix Честного знака. Сканирование камерой и сканером, распознавание GS-разделителей (F8, Alt+0029), преобразование КИЗ. Работает в браузере без регистрации.",
  url: "https://seller.anylex.ru/free-functions/chestny-znak",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web browser",
  offers: { "@type": "Offer", price: "0", priceCurrency: "RUB" },
  provider: { "@type": "Organization", name: "Энилекс", url: "https://anylex.ru" },
  featureList: [
    "Генерация Data Matrix GS1 Честного знака",
    "Печать дубля маркировки на термопринтере",
    "Сканирование КИЗ камерой и USB-сканером",
    "Распознавание GS-разделителей (F8, Alt+0029)",
    "Преобразование КИЗ в корректный формат",
    "Сохранение этикетки в PDF",
  ],
};

function MetaChestnyZnak() {
  useMeta({
    title: "Честный знак онлайн: печать и дубли Data Matrix, проверка КИЗ — Энилекс",
    description: "Бесплатный инструмент для работы с Честным знаком: печать и дублирование Data Matrix, сканирование КИЗ камерой и сканером, распознавание GS-разделителей (F8, Alt+0029), преобразование кода. Без регистрации, данные не сохраняются.",
    canonical: "https://seller.anylex.ru/free-functions/chestny-znak",
  });
  useJsonLd(CHESTNY_ZNAK_SCHEMA);
  return null;
}

function ChestnyZnakSeoSection() {
  return (
    <div className="ff__seo">
      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Как пользоваться инструментом Честного знака</h2>
        <ol className="ff__seo-list ff__seo-list--ol">
          <li>Выберите <strong>размер этикетки</strong> под ваш термопринтер (58×40, 30×30 мм и другие).</li>
          <li>Отсканируйте код маркировки <strong>камерой</strong> или <strong>USB-сканером</strong>, либо вставьте значение в поле «Значение».</li>
          <li>Проверьте поле <strong>«Преобразованное»</strong> — там КИЗ показан в корректном виде со скрытыми GS-разделителями.</li>
          <li>Опционально: добавьте название товара и даты (производство, срок годности, годен до).</li>
          <li>Нажмите <strong>«Печать»</strong> или <strong>Enter</strong> — откроется диалог принтера. Со сканером печать запускается автоматически.</li>
          <li>Для сохранения в PDF нажмите <strong>«Сохранить PDF»</strong> и выберите «Сохранить как PDF».</li>
        </ol>
        <p className="ff__seo-note">Всё обрабатывается в браузере — коды маркировки не отправляются на сервер.</p>
      </section>

      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Что такое Data Matrix Честного знака</h2>
        <div className="ff__seo-formats">
          {[
            ["Data Matrix GS1", "Двумерный код маркировки товаров в системе «Честный знак». Содержит GTIN, серийный номер и криптографическую часть, разделённые GS-символом (ASCII 29)."],
            ["КИЗ", "Код идентификации товара — уникальная строка внутри Data Matrix. Именно её нужно корректно передать при приёмке на Wildberries и Ozon."],
            ["GS-разделитель", "Невидимый символ-разделитель между группами данных. Сканер передаёт его как клавишу F8 или Alt+0029. Инструмент распознаёт оба варианта."],
            ["Криптохвост (91/92)", "Криптографический ключ проверки и значение проверки. Обязательная часть кода маркировки, следует за серийным номером после GS-разделителя."],
          ].map(([name, desc]) => (
            <div key={name} className="ff__seo-format">
              <div className="ff__seo-format-name">{name}</div>
              <div className="ff__seo-format-desc">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Когда нужен инструмент</h2>
        <ul className="ff__seo-list">
          <li><strong>Повреждена этикетка маркировки</strong> — быстро распечатайте дубль Data Matrix по коду без обращения в систему.</li>
          <li><strong>Проверка настройки сканера</strong> — убедитесь, что сканер передаёт GS-разделители до отправки КИЗ на проверку.</li>
          <li><strong>Преобразование считанного кода</strong> — получите КИЗ в корректном виде для вставки в другие программы.</li>
          <li><strong>Печать на термопринтере</strong> — этикетка Data Matrix нужного размера с названием и датами.</li>
        </ul>
      </section>

      <section className="ff__seo-section">
        <h2 className="ff__seo-h2">Часто задаваемые вопросы</h2>
        <div className="ff__seo-faq">
          {[
            ["Почему сканер передаёт код неправильно?", "Data Matrix Честного знака содержит скрытые GS-разделители. Сканер должен передавать их как F8 или Alt+0029 и работать в режиме GS1 DataMatrix при английской раскладке. Инструмент распознаёт оба варианта и показывает результат в поле «Преобразованное»."],
            ["Как проверить, правильно ли считывается КИЗ?", "Отсканируйте код в поле «Значение» и посмотрите поле «Преобразованное» — там видны скрытые GS-разделители. Если они на месте, сканер настроен верно."],
            ["Данные отправляются на сервер?", "Нет. Генерация Data Matrix и преобразование КИЗ выполняются локально в браузере. Коды маркировки никуда не передаются."],
            ["Какой размер этикетки выбрать?", "Для маркировки обычно используют квадратные форматы (25×25, 30×30 мм) или стандартные 58×40 мм с текстом. Выберите размер под ваш термопринтер."],
            ["Печатает ли инструмент несколько копий?", "Да. Укажите количество копий — каждая этикетка будет на отдельной странице."],
          ].map(([q, a]) => (
            <details key={q} className="ff__seo-qa">
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

const AI_GENERATOR_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "AI-генератор названий и описаний для маркетплейсов — Энилекс",
  description: "Бесплатный AI-инструмент для генерации SEO-названий и описаний товаров для Ozon и Wildberries через GigaChat. Используйте свой API-ключ — данные не хранятся.",
  url: "https://seller.anylex.ru/free-functions/ai-generator",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web browser",
  offers: { "@type": "Offer", price: "0", priceCurrency: "RUB" },
  provider: { "@type": "Organization", name: "Энилекс", url: "https://anylex.ru" },
};

function MetaAiGenerator() {
  useMeta({
    title: "AI-генератор названий и описаний для Ozon и Wildberries — Энилекс",
    description: "Бесплатный AI-генератор SEO-названий и описаний товаров для маркетплейсов Ozon и Wildberries. Работает через GigaChat с вашим API-ключом. Без регистрации, данные не хранятся.",
    canonical: "https://seller.anylex.ru/free-functions/ai-generator",
  });
  useJsonLd(AI_GENERATOR_SCHEMA);
  return null;
}
